import { useState } from "react";

import { translate } from "app/filters/i18nFilter";
import { isEmpty } from "lodash";
import type { SignalTimeframe } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalTimeframe";
import newsSignalTimeframeConfig from "app/pages/sales-intelligence/sub-modules/signals/models/news/NewsSignalTimeframe";
import type SignalFiltersViewProps from "app/pages/sales-intelligence/sub-modules/signals/models/view/SignalFiltersViewProps";
import { StyledModalFooter } from "app/pages/sales-intelligence/sub-modules/signals/components/modals/SignalsModal/styles";

import useSignalFiltersState from "../../../hooks/useFiltersState";
import type { NewsCategoriesByType } from "../../../models/news/NewsCategoriesByType";
import type { NewsCategory } from "../../../models/news/NewsCategory";
import type NewsSignalFilters from "../../../models/news/NewsSignalFilters";
import SignalTimeframeSelect from "../../filters/SignalTimeframeSelect/SignalTimeframeSelect";
import SignalAnyNewsFilter from "../../filters/SignalNewsFilter/SignalAnyNewsFilter";
import SignalDropdownLabel from "../../shared/SignalsDropdownLabel/SignalDropdownLabel";

import {
    StyledFiltersViewContent,
    StyledFiltersViewInner,
    StyledNotificationWrapper,
} from "../../styles";

export type AnyNewsSignalFiltersViewProps = SignalFiltersViewProps<NewsSignalFilters> & {
    newsCategories: NewsCategoriesByType;
};

const AnyNewsSignalFiltersView = (props: AnyNewsSignalFiltersViewProps) => {
    const { newsCategories, filters } = props;
    const { currentFilters, setCurrentFilters } = useSignalFiltersState(filters, props.onFiltersChange);
    const [selectedTimeframe, setSelectedTimeframe] = useState<SignalTimeframe | undefined>(() =>
        newsSignalTimeframeConfig.fromFilters(currentFilters),
    );

    const isValueEmpty = isEmpty(currentFilters.categories);

    function handleChange(categories: readonly NewsCategory[]) {
        setCurrentFilters({
            ...currentFilters,
            categories,
        });
    }

    function handleTimeframeSelect(timeframe: SignalTimeframe) {
        setSelectedTimeframe(timeframe);
        setCurrentFilters((prev) => ({ ...prev, ...newsSignalTimeframeConfig.toFilters(timeframe) }));
    }

    function handleSignalSubmit() {
        props.onFiltersSubmit({ ...filters, ...currentFilters });
    }

    return (
        <StyledFiltersViewInner alignItems="center">
            <StyledFiltersViewContent>
                <SignalDropdownLabel text={translate("si.signals.management.label.companies")}>
                    <SignalAnyNewsFilter
                        filterKey="categories"
                        isValueEmpty={isValueEmpty}
                        defaultOpen
                        selectedValue={currentFilters.categories}
                        options={newsCategories}
                        onValueChange={handleChange}
                    />
                </SignalDropdownLabel>

                {!isValueEmpty ? (
                    <SignalDropdownLabel text={translate("si.signals.management.label.timeframe")}>
                        <SignalTimeframeSelect
                            selectedTimeframe={selectedTimeframe}
                            options={newsSignalTimeframeConfig.options}
                            onSelect={handleTimeframeSelect}
                        />
                    </SignalDropdownLabel>
                ) : null}

                {props.notificationView ? <StyledNotificationWrapper>{props.notificationView}</StyledNotificationWrapper> : null}
            </StyledFiltersViewContent>

            <StyledModalFooter>
                {props.renderSubmit({
                    isDisabled: isValueEmpty || !selectedTimeframe,
                    onSubmit: handleSignalSubmit,
                })}
            </StyledModalFooter>
        </StyledFiltersViewInner>
    );
}

export default AnyNewsSignalFiltersView;
